import { BlobServiceClient } from '@azure/storage-blob';

/** Extension-based MIME lookup, matching the legacy azure-storage SDK's mime.lookup() behavior. */
const mimeTypes: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.bmp': 'image/bmp',
  '.svg': 'image/svg+xml',
  '.webp': 'image/webp',
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.wmv': 'video/x-ms-wmv',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.json': 'application/json',
  '.xml': 'application/xml',
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
  '.7z': 'application/x-7z-compressed',
};

function lookupMimeType(filename: string): string {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return mimeTypes[ext] || 'application/octet-stream';
}

export interface UploadBlobOptions {
  blockSize?: number;
  onProgress?: (percentComplete: number) => void;
}

/**
 * Upload a file to Azure Blob Storage using a SAS token.
 *
 * Replaces the legacy callback-based AzureStorage.Blob.createBlobServiceWithSas()
 * + createBlockBlobFromBrowserFile() pattern with the modern @azure/storage-blob SDK.
 */
export async function uploadBlockBlob(
  host: string,
  sasWriteToken: string,
  containerName: string,
  blobName: string,
  file: File,
  options?: UploadBlobOptions,
): Promise<void> {
  const blobServiceClient = new BlobServiceClient(`${host}?${sasWriteToken}`);
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  const blockSize = options?.blockSize ?? 1024 * 512;

  await blockBlobClient.uploadData(file, {
    blockSize,
    maxSingleShotSize: blockSize,
    blobHTTPHeaders: {
      blobContentType: file.type || lookupMimeType(file.name),
    },
    onProgress: options?.onProgress
      ? (ev) => {
          const percent = file.size > 0 ? (ev.loadedBytes / file.size) * 100 : 100;
          options.onProgress!(percent);
        }
      : undefined,
  });
}
